import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Bell, Phone, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateIST, formatRelativeTime } from '../utils/dateUtils';

const RateBook = ({ user, onLogin, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('gold');
  const [tapCount, setTapCount] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [flashingRates, setFlashingRates] = useState({});
  const [goldCostTrend, setGoldCostTrend] = useState('neutral'); // 'up', 'down', or 'neutral'
  const tapTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);
  const [baseRates, setBaseRates] = useState(null); // Store base rates from backend
  const [displayRates, setDisplayRates] = useState(null); // Rates with fluctuations applied

  // Show session replaced notification if applicable
  useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason');
    if (reason === 'session_replaced') {
      sessionStorage.removeItem('logout_reason');
      toast.error('You have been logged out because your account was logged in from another device.', { duration: 6000 });
    }
  }, []);

  // Apply small random fluctuations to make it look "live"
  const applyFluctuations = useCallback((rates) => {
    if (!rates) return null;
    
    const fluctuated = {
      ...rates,
      gold_spot: rates.gold_spot ? rates.gold_spot + (Math.random() - 0.5) * 5 : rates.gold_spot,
      usd_inr: rates.usd_inr ? rates.usd_inr + (Math.random() - 0.5) * 0.05 : rates.usd_inr,
      gold_cost: rates.gold_cost ? rates.gold_cost + Math.round((Math.random() - 0.5) * 50) : rates.gold_cost,
      gold_rates: rates.gold_rates?.map((rate, index) => ({
        ...rate,
        buy: rate.buy ? rate.buy + Math.round((Math.random() - 0.5) * 50) : rate.buy,
        buy_low: rate.buy_low ? rate.buy_low + Math.round((Math.random() - 0.5) * 50) : rate.buy_low,
        sell: rate.sell ? rate.sell + Math.round((Math.random() - 0.5) * 50) : rate.sell,
        sell_high: rate.sell_high ? rate.sell_high + Math.round((Math.random() - 0.5) * 50) : rate.sell_high,
        isSecretTrigger: index === 2 // Mark 3rd item for secret tap
      })),
      silver_rates: rates.silver_rates?.map(rate => ({
        ...rate,
        buy: rate.buy ? rate.buy + Math.round((Math.random() - 0.5) * 50) : rate.buy,
        buy_low: rate.buy_low ? rate.buy_low + Math.round((Math.random() - 0.5) * 50) : rate.buy_low,
        sell: rate.sell ? rate.sell + Math.round((Math.random() - 0.5) * 200) : rate.sell,
        sell_high: rate.sell_high ? rate.sell_high + Math.round((Math.random() - 0.5) * 200) : rate.sell_high
      }))
    };
    
    return fluctuated;
  }, []);

  const fetchLiveRates = useCallback(async () => {
    try {
      // Fetch real rates from backend API
      const response = await axiosInstance.get('/rates/latest');
      const data = response.data;
      
      if (data && data.status !== 'no_data') {
        // Store base rates and apply fluctuations
        setBaseRates(data);
        
        const fluctuatedRates = applyFluctuations(data);
        
        // Track gold cost trend for color change
        setDisplayRates(prevRates => {
          if (prevRates && prevRates.gold_cost && fluctuatedRates.gold_cost) {
            if (fluctuatedRates.gold_cost > prevRates.gold_cost) {
              setGoldCostTrend('up'); // Green
            } else if (fluctuatedRates.gold_cost < prevRates.gold_cost) {
              setGoldCostTrend('down'); // Red
            }
          }
          
          // Track which rates changed for flashing effect
          if (prevRates && prevRates.gold_rates) {
            const newFlashing = {};
            
            fluctuatedRates.gold_rates?.forEach((newRate, index) => {
              const oldRate = prevRates.gold_rates?.[index];
              if (oldRate && oldRate.sell !== newRate.sell) {
                newFlashing[`gold-${index}-sell`] = true;
                setTimeout(() => {
                  setFlashingRates(prev => {
                    const updated = { ...prev };
                    delete updated[`gold-${index}-sell`];
                    return updated;
                  });
                }, 500);
              }
              if (oldRate && oldRate.buy_low !== newRate.buy_low) {
                newFlashing[`gold-${index}-buy`] = true;
                setTimeout(() => {
                  setFlashingRates(prev => {
                    const updated = { ...prev };
                    delete updated[`gold-${index}-buy`];
                    return updated;
                  });
                }, 500);
              }
            });
            
            setFlashingRates(newFlashing);
          }
          
          return fluctuatedRates;
        });
      } else if (data && data.status === 'no_data') {
        toast.info('Waiting for rate data. Rates are fetched automatically every 2 hours.');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching live rates:', error);
      toast.error('Failed to fetch live rates');
      setLoading(false);
    }
  }, [applyFluctuations]);

  // Refresh fluctuations every 3 seconds
  useEffect(() => {
    if (baseRates) {
      intervalRef.current = setInterval(() => {
        const fluctuatedRates = applyFluctuations(baseRates);
        setDisplayRates(prevRates => {
          // Track gold cost trend
          if (prevRates && prevRates.gold_cost && fluctuatedRates.gold_cost) {
            if (fluctuatedRates.gold_cost > prevRates.gold_cost) {
              setGoldCostTrend('up');
            } else if (fluctuatedRates.gold_cost < prevRates.gold_cost) {
              setGoldCostTrend('down');
            }
          }
          return fluctuatedRates;
        });
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [baseRates, applyFluctuations]);

  // Fetch rates on component mount
  useEffect(() => {
    fetchLiveRates();
  }, [fetchLiveRates]);

  const handleSellTap = async (rateIndex) => {
    // Only trigger on 3rd item (index 2) - the "GOLD 99.99 IMPORTED (LBMA)" row
    if (rateIndex !== 2) {
      return;
    }

    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
    }

    const newTapCount = tapCount + 1;
    setTapCount(newTapCount);

    // 10 taps required to open login modal
    if (newTapCount === 10) {
      // Check time restriction before showing login modal
      if (loginUsername) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/check-time-restriction?username=${loginUsername}`);
          const data = await response.json();
          
          if (!data.allowed) {
            toast.error(data.message || 'Login not allowed at this time');
            setTapCount(0);
            return;
          }
        } catch (error) {
          console.error('Error checking time restriction:', error);
        }
      }
      
      setShowLoginModal(true);
      setTapCount(0);
    } else {
      // Reset count after 2 seconds of inactivity
      tapTimerRef.current = setTimeout(() => {
        setTapCount(0);
      }, 2000);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginUsername || !loginPassword) {
      toast.error('Please enter username and password');
      return;
    }

    try {
      // Check time restriction first
      let timeAllowed = true;
      try {
        const timeCheckResponse = await axiosInstance.post('/auth/check-time-restriction', { username: loginUsername });
        const timeCheckData = timeCheckResponse.data;
        if (!timeCheckData.allowed) {
          toast.error(timeCheckData.message || 'Login not allowed at this time');
          const restriction = timeCheckData.restriction;
          if (restriction && restriction.enabled) {
            toast.error(`Allowed time: ${restriction.start_time} - ${restriction.end_time}`);
          }
          timeAllowed = false;
        }
      } catch (timeErr) {
        // If endpoint fails, allow login (don't block on time check failure)
      }
      
      if (!timeAllowed) return;

      // Proceed with login
      const response = await axiosInstance.post('/auth/login', {
        username: loginUsername,
        password: loginPassword
      });
      
      const { access_token, user: userData } = response.data;
      sessionStorage.setItem('token', access_token);
      onLogin(userData, access_token);
      setShowLoginModal(false);
      setLoginUsername('');
      setLoginPassword('');
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '-';
    return Math.round(num).toLocaleString('en-IN');
  };

  const parseProductName = (name) => {
    // Extract main name and type/date
    const match = name.match(/(.*?)\s+(T\+0|\d+th\s+DEC|\d+th\s+JAN)?$/);
    if (match) {
      return {
        main: match[1].trim(),
        type: match[2] || ''
      };
    }
    return { main: name, type: '' };
  };

  const currentRates = activeTab === 'gold' ? (displayRates?.gold_rates || []) : (displayRates?.silver_rates || []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-850 to-gray-900 text-white pb-16">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-700 p-3 sm:p-4">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-wide">
              ARIHANT SPOT<sup className="text-xs text-gray-500 ml-0.5">®</sup>
            </h1>
          </div>
          <div className="flex gap-2 sm:gap-4">
            <button 
              onClick={() => toast.info('Option not available right now')}
              className="text-gray-400 hover:text-white p-1 sm:p-0"
            >
              <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button 
              onClick={() => toast.info('Option not available right now')}
              className="text-gray-400 hover:text-white p-1 sm:p-0"
            >
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button 
              onClick={() => toast.info('Option not available right now')}
              className="text-gray-400 hover:text-white p-1 sm:p-0"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-850 border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex">
          <button
            onClick={() => setActiveTab('gold')}
            className={`flex-1 py-3 text-xs sm:text-sm font-bold transition-all ${
              activeTab === 'gold'
                ? 'text-yellow-400 border-b-2 border-yellow-400 bg-gray-800'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            LIVE RATES
          </button>
          <button
            onClick={() => setActiveTab('silver')}
            className={`flex-1 py-3 text-xs sm:text-sm font-bold transition-all ${
              activeTab === 'silver'
                ? 'text-yellow-400 border-b-2 border-yellow-400 bg-gray-800'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            SILVER RATES
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="max-w-6xl mx-auto p-4 text-center">
          <div className="animate-pulse text-yellow-400 text-sm">Loading live rates...</div>
        </div>
      )}

      {!loading && displayRates && (
        <>
          {/* Key Rates Display - 3 Cards */}
          <div className="max-w-6xl mx-auto p-3 sm:p-4">
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
              {activeTab === 'gold' ? (
                <>
                  {/* GOLD Card */}
                  <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 text-gray-900 rounded-xl p-3 sm:p-4 text-center shadow-lg">
                    <div className="text-[9px] sm:text-xs font-bold mb-1 uppercase tracking-wide">GOLD</div>
                    <div className="text-lg sm:text-2xl font-bold">
                      {displayRates.gold_spot ? displayRates.gold_spot.toFixed(2) : '-'}
                    </div>
                  </div>
                  
                  {/* USD INR Card */}
                  <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 text-gray-900 rounded-xl p-3 sm:p-4 text-center shadow-lg">
                    <div className="text-[9px] sm:text-xs font-bold mb-1 uppercase tracking-wide">USD INR</div>
                    <div className="text-lg sm:text-2xl font-bold">
                      {displayRates.usd_inr ? displayRates.usd_inr.toFixed(3) : '-'}
                    </div>
                  </div>
                  
                  {/* GOLD COST Card - Dynamic color based on trend */}
                  <div className={`bg-gradient-to-br text-white rounded-xl p-3 sm:p-4 text-center shadow-lg transition-colors duration-500 ${
                    goldCostTrend === 'up' ? 'from-green-500 to-green-600' : 
                    goldCostTrend === 'down' ? 'from-red-500 to-red-600' : 
                    'from-red-500 to-red-600'
                  }`}>
                    <div className="text-[9px] sm:text-xs font-bold mb-1 uppercase tracking-wide">GOLD COST</div>
                    <div className="text-lg sm:text-2xl font-bold">
                      {displayRates.gold_cost ? formatNumber(displayRates.gold_cost) : '-'}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* SILVER Card */}
                  <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 text-gray-900 rounded-xl p-3 sm:p-4 text-center shadow-lg">
                    <div className="text-[9px] sm:text-xs font-bold mb-1 uppercase tracking-wide">SILVER</div>
                    <div className="text-lg sm:text-2xl font-bold">30.50</div>
                  </div>
                  
                  {/* USD INR Card */}
                  <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 text-gray-900 rounded-xl p-3 sm:p-4 text-center shadow-lg">
                    <div className="text-[9px] sm:text-xs font-bold mb-1 uppercase tracking-wide">USD INR</div>
                    <div className="text-lg sm:text-2xl font-bold">
                      {displayRates.usd_inr ? displayRates.usd_inr.toFixed(3) : '-'}
                    </div>
                  </div>
                  
                  {/* SILVER COST Card */}
                  <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl p-3 sm:p-4 text-center shadow-lg">
                    <div className="text-[9px] sm:text-xs font-bold mb-1 uppercase tracking-wide">SILVER COST</div>
                    <div className="text-lg sm:text-2xl font-bold">182405</div>
                  </div>
                </>
              )}
            </div>

            {/* Rates List */}
            <div className="space-y-0">
              {currentRates.map((rate, index) => {
                const parsed = parseProductName(rate.name);
                const isSecretTrigger = rate.isSecretTrigger;
                
                return (
                  <div
                    key={index}
                    className="bg-gray-800 border-b border-yellow-500/20 py-3 px-3 sm:px-4 hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex justify-between items-start gap-3">
                      {/* Product Name - Left side with wrapping */}
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="text-xs sm:text-sm font-semibold text-white leading-tight break-words">
                          {parsed.main}{' '}
                          {parsed.type && (
                            <span className="text-yellow-400 text-[10px] sm:text-xs font-bold whitespace-nowrap">
                              {parsed.type}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* BUY/SELL Columns - Right side, always aligned */}
                      <div className="flex gap-4 sm:gap-8 flex-shrink-0">
                        {/* BUY Column */}
                        <div className="text-right min-w-[90px] sm:min-w-[120px]">
                          <div className="text-[9px] sm:text-xs text-gray-400 font-semibold mb-1">BUY</div>
                          {rate.buy ? (
                            <>
                              <div className={`text-base sm:text-lg font-bold transition-all duration-300 ${
                                flashingRates[`gold-${index}-buy`] ? 'text-red-400 scale-110' : 'text-red-500'
                              }`}>
                                {formatNumber(rate.buy)}
                              </div>
                              {rate.buy_low && (
                                <div className="text-[10px] sm:text-xs text-red-400 mt-0.5">
                                  L : {formatNumber(rate.buy_low)}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-base sm:text-lg font-bold text-gray-600">-</div>
                          )}
                        </div>

                        {/* SELL Column */}
                        <div className="text-right min-w-[90px] sm:min-w-[120px]">
                          <div className="text-[9px] sm:text-xs text-gray-400 font-semibold mb-1">SELL</div>
                          <div
                            className={`text-base sm:text-lg font-bold transition-all duration-300 select-none ${
                              flashingRates[`gold-${index}-sell`] ? 'text-green-300 scale-110' : 'text-green-500'
                            } ${isSecretTrigger ? 'cursor-pointer hover:text-green-400' : ''}`}
                            onClick={() => handleSellTap(index)}
                            onTouchEnd={(e) => { e.preventDefault(); handleSellTap(index); }}
                          >
                            {formatNumber(rate.sell)}
                          </div>
                          {rate.sell_high && (
                            <div className="text-[10px] sm:text-xs text-green-400 mt-0.5">
                              H : {formatNumber(rate.sell_high)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Live Update Indicator */}
            <div className="mt-4 text-center text-[10px] sm:text-xs text-gray-500">
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Live rates • Updates every 3 seconds
              </span>
            </div>
          </div>
        </>
      )}

      {/* Hidden Admin Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 sm:p-8 max-w-md w-full border-2 border-yellow-500 shadow-2xl relative">
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-2 tracking-wide">ARIHANT SPOT</h2>
              <p className="text-gray-400 text-xs sm:text-sm">Admin Login</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-yellow-500 transition-colors"
                  placeholder="Enter username"
                  required
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-yellow-500 transition-colors"
                  placeholder="Enter password"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full py-2 sm:py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 font-bold rounded-lg text-sm sm:text-base hover:from-yellow-400 hover:to-yellow-500 transition-all transform hover:scale-105 shadow-lg"
              >
                LOGIN
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 p-2 text-center text-[10px] sm:text-xs text-gray-500">
        © {new Date().getFullYear()} Arihant Spot
      </div>
    </div>
  );
};

export default RateBook;
